<template>
  <transition name="ui-modal-fade">
    <div
      v-if="value"
      class="ui-modal-overlay"
      @mousedown.self="handleBackdropClick"
      @keydown.esc="handleEsc"
    >
      <div
        ref="dialog"
        class="ui-modal"
        :class="`ui-modal--${size}`"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? titleId : null"
        tabindex="-1"
        @keydown.tab="onTabKeydown"
      >
        <div class="ui-modal__header">
          <h2 v-if="title" :id="titleId" class="ui-modal__title">{{ title }}</h2>
          <button
            v-if="closable"
            type="button"
            class="ui-modal__close"
            aria-label="סגור"
            @click="close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="ui-modal__body">
          <slot></slot>
        </div>
        <div v-if="hasFooter" class="ui-modal__footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
let uid = 0;
const FOCUSABLE = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"]):not([disabled])';

export default {
  name: 'UiModal',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: '' },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    closable: { type: Boolean, default: true },
  },
  data() {
    uid++;
    return {
      titleId: `ui-modal-title-${uid}`,
      previousFocus: null,
    };
  },
  watch: {
    value(open) {
      if (open) {
        this.previousFocus = document.activeElement;
        document.body.classList.add('ui-modal-open');
        this.$nextTick(() => this.trapFocus());
      } else {
        document.body.classList.remove('ui-modal-open');
        if (this.previousFocus && this.previousFocus.focus) {
          this.previousFocus.focus();
        }
      }
    },
  },
  beforeDestroy() {
    document.body.classList.remove('ui-modal-open');
  },
  computed: {
    hasFooter() {
      return !!this.$slots.footer;
    },
  },
  methods: {
    close() {
      this.$emit('input', false);
      this.$emit('close');
    },
    handleBackdropClick() {
      if (this.closable) this.close();
    },
    handleEsc() {
      if (this.closable) this.close();
    },
    trapFocus() {
      const el = this.$refs.dialog;
      if (!el) return;
      const focusable = el.querySelectorAll(FOCUSABLE);
      if (focusable.length) {
        focusable[0].focus();
      } else {
        el.focus();
      }
    },
    onTabKeydown(event) {
      const el = this.$refs.dialog;
      if (!el) return;
      const focusables = Array.from(el.querySelectorAll(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ui-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.75);
  padding: var(--space-4);
}

.ui-modal {
  position: relative;
  background: var(--color-surface1);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - var(--space-8));
  overflow: hidden;
  outline: none;
}

.ui-modal--sm { width: min(380px, 100%); }
.ui-modal--md { width: min(560px, 100%); }
.ui-modal--lg { width: min(640px, 100%); }

.ui-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-6) var(--space-6) 0;
  gap: var(--space-4);
}

.ui-modal__title {
  margin: 0;
  font-size: 22px;
  font-weight: var(--weight-bold);
  color: var(--color-text);
  letter-spacing: -0.2px;
  line-height: 1.3;
}

.ui-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 44px;
  min-height: 44px;
  margin-inline-start: auto;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  color: var(--color-text);
  cursor: pointer;
  flex-shrink: 0;
  transition: background var(--duration-fast) var(--easing-standard);
}

.ui-modal__close:hover {
  background: rgba(255, 255, 255, 0.1);
}

.ui-modal__close:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

.ui-modal__body {
  padding: var(--space-6);
  overflow-y: auto;
  flex: 1;
  color: var(--color-text);
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
}

.ui-modal__footer {
  padding: 0 var(--space-6) var(--space-6);
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  border-block-start: 1px solid var(--color-border);
  padding-block-start: var(--space-4);
}

/* ── Transition ── */
.ui-modal-fade-enter-active,
.ui-modal-fade-leave-active {
  transition: opacity var(--duration-slow) var(--easing-standard);
}
.ui-modal-fade-enter,
.ui-modal-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .ui-modal-fade-enter-active,
  .ui-modal-fade-leave-active {
    transition: none;
  }
  .ui-modal__close {
    transition: none;
  }
}
</style>

<!-- Global style — body scroll lock, not scoped -->
<style lang="scss">
body.ui-modal-open {
  overflow: hidden;
}
</style>
